// THE STANDARD — usePushNotifications
// Manages Web Push subscription state for the current user.
//
// Rule: NEVER show error toasts for permission denied or push service
// unavailable. Those are user choices or environment limitations — fail
// silently always.
//
// One exception: iOS Safari tabs. iOS web push is hard-gated on the app
// being installed to Home Screen; toggling permission inside a Safari
// tab creates a subscription that NEVER receives push. We surface a
// targeted toast in that case so the user gets a clear next step
// instead of a silent dead end.
//
// Subscription model:
// - On enable: tear down any pre-existing PushSubscription so we always
//   register a fresh one against the current VAPID key. Reusing a stale
//   subscription is the most common cause of "permission says granted
//   but pushes never land".
// - On tab visible: hit push.checkSubscription. If the server has zero
//   rows for us but the browser still has a PushSubscription, the SW
//   was wiped (clear-site-data, OS storage cleanup) and we resubscribe.
// - All push state changes are mirrored to the server via
//   push.syncPermissionState so the coach diagnostics view is current.

import { useState, useEffect, useCallback } from 'react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { needsIOSHomeScreenInstall, isIOSDevice, isStandalonePWA } from '@/lib/platform';
import { Capacitor } from '@capacitor/core';
import { ensureCoachDmNotificationChannel } from '@/lib/coachDmChannel';

// Production domains where push is fully supported
const PRODUCTION_DOMAINS = ['setthestandard.fit', 'www.setthestandard.fit'];
const isProduction = typeof window !== 'undefined' && PRODUCTION_DOMAINS.includes(window.location.hostname);

// Errors we always swallow silently — they indicate the environment isn't
// capable of push (sandbox preview, denied permission, no SW). Never useful
// to surface to the end user.
const isPushUnavailable = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  return (
    msg.includes('push service not available') ||
    msg.includes('registration failed') ||
    msg.includes('not available') ||
    msg.includes('network error') ||
    msg.includes('failed to fetch') ||
    msg.includes('aborted') ||
    msg.includes('permission') ||
    msg.includes('denied')
  );
};

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

type PermissionState = 'default' | 'granted' | 'denied';

// APNs/FCM rotate device tokens whenever they like (app reinstall, OS
// update, Apple-side refresh). A token registered weeks ago may be dead
// even though the OS still says permission=granted, so we must call
// PushNotifications.register() on every app launch to push the *current*
// token to the server. Module-level guard: the hook mounts in several
// components (layouts, settings, banners) and we only want one register()
// per app session.
let nativeTokenRefreshedThisSession = false;
// CoachLayout can call register() before this hook attaches the registration
// listener. iOS delivers the token once into that gap and the server never
// stores it. subscribe() waits on this gate.
let resolveNativeListeners = () => {};
let nativeListenersReady = new Promise<void>((resolve) => {
  resolveNativeListeners = resolve;
});
let listenerGeneration = 0;

// App icon badge hygiene. APNs sets the icon badge via the push payload's
// `badge` field but nothing on-device ever resets it, so the count sticks at
// whatever the last push said (the classic "icon stuck on 5" report). Clear
// it whenever the app opens or returns to the foreground. Module-level guard
// for the same reason as nativeTokenRefreshedThisSession: the hook mounts in
// several components and the appStateChange/visibilitychange listeners must
// only be attached once per app session (they intentionally live for the
// whole session, so no cleanup).
let badgeClearAttachedThisSession = false;

async function clearAppIconBadge(isNative: boolean): Promise<void> {
  try {
    if (isNative) {
      // Dynamic import + try/catch: silent no-op on binaries built before
      // the Badge plugin shipped. Do NOT clear delivered notifications here —
      // only the icon badge count.
      const { Badge } = await import('@capawesome/capacitor-badge');
      await Badge.clear();
    } else {
      await (navigator as Navigator & { clearAppBadge?: () => Promise<void> }).clearAppBadge?.();
    }
  } catch {
    // Silent — plugin unavailable or Badging API unsupported.
  }
}

function readPermission(): PermissionState {
  if (typeof Notification === 'undefined') return 'default';
  return Notification.permission as PermissionState;
}

export function usePushNotifications() {
  const isNativeApp = Capacitor.isNativePlatform();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState<PermissionState>(readPermission());
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [isIOS] = useState<boolean>(() => isIOSDevice());
  const [isStandalone] = useState<boolean>(() => isStandalonePWA());

  const utils = trpc.useUtils();
  const { data: statusData } = trpc.push.status.useQuery();

  const subscribeMutation = trpc.push.subscribe.useMutation({
    onSuccess: () => {
      setIsSubscribed(true);
      setSubscriptionExpired(false);
      utils.push.status.invalidate();
      utils.push.checkSubscription.invalidate();
      if (!isProduction) return;
      // Honest UX on iOS: iOS Safari tabs don't deliver push even when
      // permission is granted. Tell the user the truth so they install
      // to Home Screen instead of waiting for pushes that will never come.
      if (needsIOSHomeScreenInstall()) {
        toast.warning('Install to Home Screen to receive notifications', {
          description: 'iOS Safari tabs cannot receive push. Tap Share → Add to Home Screen, then open the app from there.',
          duration: 9000,
        });
      } else {
        toast.success('Push notifications enabled');
      }
    },
    onError: () => {
      // Silent — subscription save failure is not actionable by the user
    },
  });

  const unsubscribeMutation = trpc.push.unsubscribe.useMutation({
    onSuccess: () => {
      setIsSubscribed(false);
      utils.push.status.invalidate();
      utils.push.checkSubscription.invalidate();
      if (isProduction) toast.success('Push notifications disabled');
    },
    onError: () => {
      // Silent
    },
  });

  const syncPermissionMutation = trpc.push.syncPermissionState.useMutation();
  const nativeRegisterMutation = trpc.push.registerNative.useMutation({
    onSuccess: () => {
      setIsSubscribed(true);
      setSubscriptionExpired(false);
      utils.push.status.invalidate();
      utils.push.checkSubscription.invalidate();
      if (isProduction) toast.success('Push notifications enabled');
    },
    onError: () => {
      // Silent — token save failure is not actionable by the user
    },
  });
  const nativeUnregisterMutation = trpc.push.unregisterNative.useMutation({
    onSuccess: () => {
      setIsSubscribed(false);
      utils.push.status.invalidate();
      utils.push.checkSubscription.invalidate();
      if (isProduction) toast.success('Push notifications disabled');
    },
    onError: () => {
      // Silent
    },
  });

  // Async support detection. iOS Safari only exposes PushManager via
  // `serviceWorkerRegistration.pushManager`, never on `window`, so the
  // sync `'PushManager' in window` test (used by every other tutorial)
  // returns false on iOS PWAs that DO support push. Get a registration
  // first, then check pushManager on it.
  useEffect(() => {
    let cancelled = false;

    async function detect() {
      if (isNativeApp) {
        if (!cancelled) setIsSupported(true);
        return;
      }
      const hasSW = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
      const hasNotif = typeof window !== 'undefined' && 'Notification' in window;
      if (!hasSW || !hasNotif) {
        if (!cancelled) setIsSupported(false);
        return;
      }
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (cancelled) return;
        // Wait for the registration to come up if the SW is still in the
        // workbox install/activate cycle on first load.
        const ready = reg ?? (await navigator.serviceWorker.ready.catch(() => null));
        if (cancelled) return;
        setIsSupported(!!ready?.pushManager);
      } catch {
        if (!cancelled) setIsSupported(false);
      }
    }

    detect();
    return () => { cancelled = true; };
  }, [isNativeApp]);

  useEffect(() => {
    if (!isNativeApp) return;
    void ensureCoachDmNotificationChannel();
    const generation = ++listenerGeneration;
    let cancelled = false;
    const handles: Array<{ remove: () => Promise<void> } | { remove: () => void }> = [];

    async function registerListeners() {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const perms = await PushNotifications.checkPermissions();
        if (!cancelled) {
          const next: PermissionState = perms.receive === 'granted'
            ? 'granted'
            : perms.receive === 'denied'
              ? 'denied'
              : 'default';
          setPermission(next);
        }

        handles.push(await PushNotifications.addListener('registration', async (token) => {
          try {
            await nativeRegisterMutation.mutateAsync({
              platform: Capacitor.getPlatform() === 'android' ? 'android' : 'ios',
              token: token.value,
              environment: 'production',
              deviceLabel: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 128) : undefined,
            });
          } catch {
            // Mutation handles failure silently.
          }
        }));
        handles.push(await PushNotifications.addListener('registrationError', (error) => {
          setIsSubscribed(false);
          console.warn('[Push] Native registration failed:', error);
        }));
        handles.push(await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          const data = notification.data ?? {};
          const title = notification.title || 'The Standard';
          const body = notification.body || 'New update ready.';
          const rawUrl = typeof data.url === 'string' ? data.url : '/';
          const url = rawUrl.startsWith('/') ? rawUrl : '/';
          const tag = typeof data.tag === 'string' ? data.tag : title;
          const toastId = `native-push-${notification.id || tag}`;

          toast(title, {
            id: toastId,
            description: body,
            duration: 9000,
            action: {
              label: 'Open',
              onClick: () => {
                window.location.href = url;
              },
            },
          });
        }));
        handles.push(await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
          const data = event.notification?.data ?? {};
          const url = typeof data.url === 'string' ? data.url : '/';
          window.location.href = url.startsWith('/') ? url : '/';
        }));

        // Token refresh on launch. Must happen AFTER the 'registration'
        // listener above is attached, or the token event fires into the
        // void and the server never learns the device's current token.
        // register() never prompts — when permission is already granted it
        // just re-fetches the token from APNs/FCM.
        if (!cancelled && !nativeTokenRefreshedThisSession && perms.receive === 'granted') {
          nativeTokenRefreshedThisSession = true;
          try {
            await PushNotifications.register();
          } catch (err) {
            console.warn('[Push] Native token refresh on launch failed:', err);
          }
        }
      } catch (err) {
        console.warn('[Push] Native listener setup failed:', err);
      } finally {
        if (generation === listenerGeneration) resolveNativeListeners();
      }
    }

    void registerListeners();
    return () => {
      cancelled = true;
      for (const handle of handles) {
        try { void handle.remove(); } catch { /* noop */ }
      }
    };
    // Register native listeners once for this app session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNativeApp]);

  // Clear the app icon badge on launch and whenever the app becomes active
  // again. Native: Capacitor App appStateChange. Web: visibilitychange +
  // navigator.clearAppBadge (mirrors the setAppBadge call in push-sw.js).
  useEffect(() => {
    if (badgeClearAttachedThisSession) return;
    badgeClearAttachedThisSession = true;

    void clearAppIconBadge(isNativeApp);

    if (isNativeApp) {
      (async () => {
        try {
          const { App } = await import('@capacitor/app');
          await App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) void clearAppIconBadge(true);
          });
        } catch {
          // Silent — listener is best-effort; the on-mount clear above still ran.
        }
      })();
    } else if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void clearAppIconBadge(false);
      });
    }
    // Attach once per app session (module-level guard above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track Notification.permission changes. The Permissions API gives us a
  // proper change event on Chrome/Firefox; iOS Safari doesn't, so we also
  // re-read on visibilitychange below.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('permissions' in navigator)) return;
    let cancelled = false;
    let status: PermissionStatus | null = null;
    const handler = () => {
      const next = readPermission();
      setPermission(next);
      // Mirror to server so coach diagnostics is fresh.
      syncPermissionMutation.mutate({ permissionState: next });
    };
    (navigator.permissions as Permissions & { query: (d: PermissionDescriptor) => Promise<PermissionStatus> })
      .query({ name: 'notifications' as PermissionName })
      .then((s) => {
        if (cancelled) return;
        status = s;
        s.addEventListener('change', handler);
      })
      .catch(() => { /* unsupported in this browser — fall back to visibility-driven sync */ });
    return () => {
      cancelled = true;
      status?.removeEventListener('change', handler);
    };
    // syncPermissionMutation is stable across renders — intentional empty deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconcile server status with the actual SW subscription. If the server
  // says the user is subscribed but the SW has no PushSubscription, the
  // browser dropped it on its own (storage eviction, profile reset) — we
  // mark expired so the auto-recover effect can resubscribe.
  useEffect(() => {
    if (!statusData) return;
    if (isNativeApp) {
      setIsSubscribed((statusData.nativeCount ?? 0) > 0);
      setSubscriptionExpired(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker?.getRegistration();
        if (cancelled) return;
        const sub = (await reg?.pushManager.getSubscription()) ?? null;
        const liveSub = !!sub;
        const serverHasWebSubscription = (statusData.webCount ?? statusData.count ?? 0) > 0;
        setIsSubscribed(liveSub && serverHasWebSubscription);
        setSubscriptionExpired(
          (serverHasWebSubscription && !liveSub) ||
          (!serverHasWebSubscription && (liveSub || permission === 'granted')),
        );
      } catch {
        if (!cancelled) {
          const serverHasWebSubscription = (statusData.webCount ?? statusData.count ?? 0) > 0;
          setIsSubscribed(serverHasWebSubscription);
          setSubscriptionExpired(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [statusData, isNativeApp, permission]);

  // Wait for whatever SW workbox registered (we no longer register a second
  // /push-sw.js — push handlers are imported into the workbox SW via
  // workbox.importScripts, so there's exactly one registration at scope /).
  async function getActiveRegistration(): Promise<ServiceWorkerRegistration | null> {
    try {
      const existing = await navigator.serviceWorker?.getRegistration();
      if (existing) return existing;
      return await navigator.serviceWorker.ready;
    } catch {
      return null;
    }
  }

  const subscribe = useCallback(async () => {
    if (isNativeApp) {
      setIsLoading(true);
      try {
        await nativeListenersReady;
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const requested = await PushNotifications.requestPermissions();
        const next: PermissionState = requested.receive === 'granted'
          ? 'granted'
          : requested.receive === 'denied'
            ? 'denied'
            : 'default';
        setPermission(next);
        try { await syncPermissionMutation.mutateAsync({ permissionState: next }); } catch { /* noop */ }
        if (next !== 'granted') return;
        await PushNotifications.register();
      } catch (err) {
        console.warn('[Push] Native subscribe failed:', err);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    setIsLoading(true);
    try {
      const requested = await Notification.requestPermission();
      const next = requested as PermissionState;
      setPermission(next);
      // Always mirror the latest permission state to the server, even on
      // denial — the coach view needs to see "user denied" not "user has
      // no row". Best-effort: never block on this.
      try { await syncPermissionMutation.mutateAsync({ permissionState: next }); } catch { /* noop */ }
      if (next !== 'granted') return;

      const reg = await getActiveRegistration();
      if (!reg) return; // Sandbox/preview — silent

      // Always tear down any pre-existing subscription. Reusing one that
      // was created against an old VAPID key (or after the SW was reset)
      // produces a "subscribed but no pushes ever arrive" state that's
      // basically impossible to debug from the browser. A single fresh
      // subscribe is cheap.
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        try { await existing.unsubscribe(); } catch { /* tolerate */ }
      }

      // Pull the current VAPID public key from the server. Doing this on
      // every subscribe (vs. baking it into the build at VITE_VAPID_PUBLIC_KEY)
      // means a key rotation takes effect without redeploying the client.
      const { publicKey } = await utils.push.vapidPublicKey.fetch();
      if (!publicKey) return;

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
      });

      const json = subscription.toJSON();
      await subscribeMutation.mutateAsync({
        endpoint: json.endpoint!,
        p256dh: json.keys!.p256dh,
        auth: json.keys!.auth,
        userAgent: navigator.userAgent.slice(0, 512),
      });
    } catch (err) {
      if (isPushUnavailable(err)) return;
      if (isProduction) console.warn('[Push] Unexpected subscribe error:', err);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unsubscribe = useCallback(async () => {
    if (isNativeApp) {
      setIsLoading(true);
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        try { await PushNotifications.unregister(); } catch { /* tolerate */ }
        await nativeUnregisterMutation.mutateAsync({});
      } catch {
        // Silent
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    setIsLoading(true);
    try {
      const reg = await navigator.serviceWorker?.getRegistration();
      let endpoint = '';
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          endpoint = sub.endpoint;
          try { await sub.unsubscribe(); } catch { /* tolerate */ }
        }
      }
      await unsubscribeMutation.mutateAsync(endpoint ? { endpoint } : {});
    } catch {
      // Silent
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = useCallback(async () => {
    if (isSubscribed) await unsubscribe();
    else await subscribe();
  }, [isSubscribed, subscribe, unsubscribe]);

  // Auto-recovery: when the user re-focuses the tab, ask the server whether
  // it has a live subscription for us. If not but the browser thinks we're
  // permitted, resubscribe automatically. Without this, the typical "I cleared
  // my browser data and now nothing arrives" flow stays broken until the user
  // toggles the bell off and on again.
  useEffect(() => {
    if (isNativeApp) return;
    const onVisible = async () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      // Pick up any permission changes the OS made while we were backgrounded
      // (iOS revoke-on-inactivity, user changes in site settings).
      const fresh = readPermission();
      if (fresh !== permission) {
        setPermission(fresh);
        try { await syncPermissionMutation.mutateAsync({ permissionState: fresh }); } catch { /* noop */ }
      }
      if (fresh !== 'granted') return;
      try {
        const result = await utils.push.checkSubscription.fetch();
        if (result.expired) {
          setSubscriptionExpired(true);
          await subscribe();
        }
      } catch {
        // Network blip — try again next visibility cycle
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    // Also try once on mount: granted-but-not-subscribed is the canonical
    // "user enabled push on another tab and this one needs to catch up" case.
    if (permission === 'granted' && subscriptionExpired) {
      void subscribe();
    }
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, subscriptionExpired]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscriptionExpired,
    isIOS,
    isStandalone,
    subscribe,
    unsubscribe,
    toggle,
  };
}
